/**
 * Shared helpers for floor/level ordering and grouping unit rows.
 */

export const sortLevelsByName = (levels = []) =>
  [...levels].sort((a, b) =>
    String(a?.name ?? '').localeCompare(String(b?.name ?? ''), undefined, {
      numeric: true,
      sensitivity: 'base'
    })
  );

/**
 * Group unit rows by floor/level. Keeps original relative order within each floor.
 * @param {Array} rows - display rows (must have `id`)
 * @param {Array} originals - source units with `id`, `level_id` / `level`
 * @returns {{ key: string, levelId: number|null, name: string, blockName: string, rows: Array }[]}
 */
export const groupRowsByFloor = (rows = [], originals = []) => {
  const originalById = new Map(
    (originals || []).map((unit) => [unit.id, unit])
  );

  const groups = new Map();
  const order = [];

  for (const row of rows || []) {
    const original = originalById.get(row.id);
    const levelId =
      original?.level_id ??
      original?.level?.id ??
      null;
    const name =
      original?.level?.name != null && String(original.level.name).trim() !== ''
        ? String(original.level.name)
        : levelId != null
          ? `Floor ${levelId}`
          : 'Unassigned';
    const key = levelId != null ? `level-${levelId}` : 'level-none';
    const blockName = String(original?.block?.name ?? '');

    if (!groups.has(key)) {
      groups.set(key, { key, levelId, name, blockName, rows: [] });
      order.push(key);
    }
    groups.get(key).rows.push(row);
  }

  const unsorted = order.map((key) => groups.get(key));
  const collator = { numeric: true, sensitivity: 'base' };
  return unsorted.sort(
    (a, b) =>
      String(a.blockName).localeCompare(String(b.blockName), undefined, collator) ||
      String(a.name).localeCompare(String(b.name), undefined, collator)
  );
};

/** Default: all floors expanded (current behavior until user collapses). */
export const buildExpandedFloorSet = (groups, collapsedKeys = []) => {
  const collapsed = new Set(collapsedKeys);
  const next = new Set();
  for (const group of groups || []) {
    if (!collapsed.has(group.key)) {
      next.add(group.key);
    }
  }
  return next;
};

/**
 * Derive a level/floor label from a unit name like "01-08" → "01".
 */
export const deriveLevelFromUnitName = (unitName) => {
  if (unitName == null || unitName === '' || unitName === '-') return null;
  const match = String(unitName).trim().match(/^([A-Za-z0-9]+)\s*[-/]/);
  if (match) return match[1];
  const digits = String(unitName).trim().match(/^(\d+)/);
  return digits ? digits[1] : null;
};

/**
 * Group invoice/deal rows by Block (A→Z) then Level (numeric), for collapsible tree tables.
 * Prefers `block` / `level` on each row; falls back to parsing `unit` name.
 * @returns {{ key: string, name: string, unitCount: number, levels: { key: string, name: string, rows: Array }[] }[]}
 */
export const groupRowsByBlockAndLevel = (rows = []) => {
  const blocks = new Map();

  for (const row of rows || []) {
    const blockName =
      row?.block != null && String(row.block).trim() !== '' && row.block !== '-'
        ? String(row.block).trim()
        : 'Unassigned';
    const blockKey = `block-${blockName.toLowerCase()}`;

    const levelFromRow =
      row?.level != null && String(row.level).trim() !== '' && row.level !== '-'
        ? String(row.level).trim()
        : null;
    const levelName =
      levelFromRow ||
      deriveLevelFromUnitName(row?.unit) ||
      'Unassigned';
    const levelKey = `${blockKey}-level-${String(levelName).toLowerCase()}`;

    if (!blocks.has(blockKey)) {
      blocks.set(blockKey, {
        key: blockKey,
        name: blockName,
        levels: new Map(),
        unitCount: 0
      });
    }

    const block = blocks.get(blockKey);
    if (!block.levels.has(levelKey)) {
      block.levels.set(levelKey, {
        key: levelKey,
        name: levelName,
        rows: []
      });
    }
    block.levels.get(levelKey).rows.push(row);
    block.unitCount += 1;
  }

  return [...blocks.values()]
    .map((block) => ({
      key: block.key,
      name: block.name,
      unitCount: block.unitCount,
      levels: [...block.levels.values()].sort((a, b) =>
        String(a.name).localeCompare(String(b.name), undefined, {
          numeric: true,
          sensitivity: 'base'
        })
      )
    }))
    .sort((a, b) =>
      String(a.name).localeCompare(String(b.name), undefined, {
        numeric: true,
        sensitivity: 'base'
      })
    );
};
